import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../authConfig';

// Get API URL from environment variables
// `??` (not `||`) so an explicit empty string is honored as same-origin;
// undefined (local dev) still falls back to the local backend.
const API_URL = process.env.REACT_APP_BASE_URL ?? 'http://localhost:5000';

// Azure AD access restriction (SCINFRA-686) - main.py independently validates
// this ID token's signature/issuer/audience, so every /api/* call (aside from
// /api/v1/submit-responses, which carries its own separate product bearer
// JWT and isn't sent through this client) needs it attached.
async function getAuthToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    }
    return null;
  }
}

// Types
export interface ScoringRequestParams {
  batch_size?: number;
  random_sample?: boolean;
  output_prefix?: string;
  grade?: number;
  item_name?: string;
  max_samples?: number;
}

// Product Management (SCINFRA-1088)
export interface Product {
  product_id: string;
  name: string;
  active: boolean;
  default_callback_url?: string | null;
  secret_arn?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface ProductSecretResponse {
  status: string;
  product: Product;
  secret: string;   // shown once — on create / rotate only
  message: string;
}

export interface ResultsQueryParams {
  page?: number;
  limit?: number;
  min_score?: number;
  max_score?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface ScoringResponse {
  status: string;
  rows_processed: number;
  output_path: string;
  timestamp: string;
}

export interface ResultsResponse {
  total: number;
  page: number;
  total_pages: number;
  limit: number;
  data: Array<any>;
}

export interface StatsResponse {
  file: string;
  total_records: number;
  score_stats: {
    count: number;
    mean: number;
    median: number;
    min: number;
    max: number;
    std: number;
    distribution: Record<string, number>;
  };
  status_stats: {
    counts: Record<string, number>;
    success_rate: number;
  };
  time_stats: {
    first: string;
    last: string;
  };
}

export interface SampleResponse {
  count: number;
  data: Array<any>;
}

export interface RandomRecord {
  gradeLevel: number;
  readingPassage: string;
  questionPrompt: string;
  studentResponse: string;
}

export interface ScoringResult {
  requestId: string;
  traits: Array<{
    trait_name: string;
    trait_score: number;
    trait_explanation: string;
    description_score: number;
    description_explanation: string;
  }>;
  score: number;
  explanation: string;
  confidence: number;
  status: string;
  raw_response: string;
}

export interface RubricData {
  rubrics: Array<{
    grade: number;
    trait: {
      description: string;
      scoring_levels: Array<{
        points: number;
        performance_criteria: string;
      }>;
    };
  }>;
}

export interface ModelConfig {
  status: string;
  model: {
    id: string;
    name: string;
    temperature: number;
    max_tokens: number;
    region: string;
  };
}

export interface UploadUrlResponse {
  status: string;
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  expires_in: number;
  message?: string;
  existing_key?: string;
  existing_parquet_key?: string;
}

export interface UploadCompleteResponse {
  status: string;
  message: string;
  s3_key: string;
  parquet_key: string;
  dataset_id: string;
}

export interface Dataset {
  key: string;
  s3_key?: string;  // Alias for key (some components use s3_key)
  filename: string;
  name?: string;  // Display name for the dataset
  size: number;
  upload_date: string;
  type: 'csv' | 'parquet' | 'unknown';
  hash?: string;
  parquet_key?: string;
  file_type?: 'items' | 'responses' | 'unknown';
  used_in_parquet?: string;
  relationships?: {
    source_files: string[];
    join_config: Record<string, any>;
    column_mappings: Record<string, any>;
    file_type: string;
    created_from: string;
  };
  source_files?: string[];
  join_config?: Record<string, any>;
  column_mappings?: Record<string, any>;
}

export interface MultiUploadFile {
  filename: string;
  file_type: 'items' | 'responses';
  file_hash?: string;
  content_type?: string;
}

export interface MultiUploadUrlResponse {
  status: string;
  files: Array<{
    filename: string;
    file_type: 'items' | 'responses';
    status: string;
    upload_url?: string;
    fields?: Record<string, string>;
    s3_key: string;
    expires_in?: number;
    existing_key?: string;
    existing_parquet_key?: string;
  }>;
}

export interface MultiUploadCompleteRequest {
  files: Array<{
    s3_key: string;
    file_type: 'items' | 'responses';
    file_hash?: string;
  }>;
  join_config: {
    join_type: 'inner' | 'left' | 'right' | 'outer';
    join_keys: {
      items: string[];
      responses: string[];
    };
  };
  column_mappings: {
    items: Record<string, string>;
    responses: Record<string, string>;
  };
  dataset_name?: string;  // Optional dataset name
  split_config?: {
    enabled: boolean;
    method: 'row_count' | 'join_uniqueness';
    rows_per_file?: number; // For row_count method
  };
}

export interface MultiUploadCompleteResponse {
  status: string;
  message: string;
  files: string[];
  parquet_key: string;
  dataset_id: string;
}

export interface DatasetsResponse {
  status: string;
  count: number;
  datasets: Dataset[];
}

export interface DatasetMetadata {
  key: string;
  filename: string;
  size: number;
  content_type: string;
  upload_date: string;
  etag: string;
  hash?: string;
  type: 'csv' | 'parquet' | 'unknown';
  parquet_key?: string;
  conversion_status?: 'completed' | 'pending';
}

export interface EvaluationResult {
  eval_runid: string;
  timestamp: string;
  rows_processed: number;
  qwk_scores: {
    [key: string]: number;
  };
  s3_parquet_path: string;
  s3_qwk_path: string;
  dataset_s3_path?: string;
  size: number;
  upload_date: string;
}

export interface EvaluationResultsResponse {
  status: string;
  count: number;
  results: EvaluationResult[];
}

// Job-related interfaces
export interface EvaluationJob {
  job_id: string;
  job_type?: 'wp' | 'cr';  // Job type: WP or CR
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  started_at?: string;
  completed_at?: string;
  progress: {
    total_rows?: number;
    rows_processed: number;
    current_batch: number;
    total_batches?: number;
    progress_percentage: number;
  };
  config: {
    batch_size: number;
    random_sample: boolean;
    max_samples?: number;
    dataset_s3_path?: string;
    output_prefix?: string;
    // CR-specific config
    max_concurrent?: number;
    prompt_template?: string;
    temperature?: number;
    filter_query?: string;
    sample_enabled?: boolean;
    sample_size?: number;
  };
  results?: {
    eval_runid: string;
    s3_parquet_path: string;
    s3_qwk_path?: string;
    qwk_scores?: Record<string, number>;
    qwk_data?: Record<string, any>;  // CR comprehensive QWK analysis
    partial_results_s3_path?: string;  // For failed jobs with partial results
  };
  error?: {
    message: string;
    traceback?: string;
  };
}

export interface CreateEvaluationResponse {
  status: string;
  job_id: string;
  created_at: string;
  message: string;
}

export interface EvaluationJobResponse {
  status: string;
  job: EvaluationJob;
}

export interface EvaluationListResponse {
  status: string;
  total: number;
  page: number;
  total_pages: number;
  limit: number;
  jobs: Array<{
    job_id: string;
    status: string;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    progress_percentage: number;
    rows_processed: number;
    total_rows?: number;
    config: {
      batch_size: number;
      max_samples?: number;
      dataset_s3_path?: string;
    };
  }>;
}

export interface EvaluationMetadataResponse {
  status: string;
  metadata: EvaluationResult;
}

// ============================================================================
// CR (Constructed Response) Evaluation Types
// ============================================================================

/**
 * Column mapping from required CR columns to source dataset columns
 */
export interface CRColumnMapping {
  [requiredColumn: string]: string;
}

/**
 * CR dataset validation response
 */
export interface CRValidationResponse {
  status: string;
  valid: boolean;
  missing_columns?: string[];
  available_columns?: string[];
  optional_columns_present?: string[];
}

/**
 * CR column mapping validation response
 */
export interface CRMappingValidationResponse {
  status: string;
  valid: boolean;
  preview_data?: Array<Record<string, any>>;
  errors?: string[];
}

/**
 * CR column mapping save response
 */
export interface CRSaveMappingResponse {
  status: string;
  mapped_s3_key: string;
  mapped_s3_path: string;
  mapping_metadata_key: string;
}

/**
 * CR evaluation parameters
 */
export interface CREvaluationParams {
  dataset_s3_path: string;
  batch_size: number;
  max_concurrent: number;
  prompt_template: string;
  temperature: number;
  filter_query?: string;
  sample_enabled: boolean;
  sample_size?: number;
  output_prefix?: string;
  model_id?: string;
  thinking_enabled?: boolean;
  thinking_budget?: number;
}

/**
 * CR job creation response
 */
export interface CRJobCreationResponse {
  status: string;
  job_id: string;
  created_at: string;
  message: string;
}

/**
 * CR prompt template info
 */
export interface CRPromptTemplate {
  name: string;
  description: string;
}

/**
 * CR prompt templates list response
 */
export interface CRPromptTemplatesResponse {
  status: string;
  templates: CRPromptTemplate[];
}

/**
 * CR create prompt template params
 */
export interface CRCreateTemplateParams {
  name: string;
  description: string;
  content: string;
}

/**
 * CR create prompt template response
 */
export interface CRCreateTemplateResponse {
  status: string;
  message: string;
}

/**
 * CR model info
 */
export interface CRModel {
  id: string;
  name: string;
}

/**
 * CR models list response
 */
export interface CRModelsResponse {
  status: string;
  models: CRModel[];
}

/**
 * Bedrock model info, with thinking-capability metadata (backs the
 * per-template model config feature's model-selection dropdown)
 */
export interface BedrockModel {
  id: string;
  name: string;
  supports_thinking: boolean;
}

/**
 * Bedrock models list response
 */
export interface BedrockModelsResponse {
  status: string;
  models: BedrockModel[];
  default_model_id: string;
}

/**
 * QWK comprehensive analysis results
 */
export interface QWKAnalysisResults {
  overall: QWKMetrics;
  grade_breakdown?: Record<string, QWKMetrics>;
  characteristic_analysis?: Record<string, Record<string, QWKMetrics>>;
}

/**
 * QWK metrics for a group
 */
export interface QWKMetrics {
  qwk_calculated: boolean;
  qwk?: number;
  cohen_kappa?: number;
  mae?: number;
  correlation?: number;
  correlation_p_value?: number;
  exact_match_rate?: number;
  exact_matches?: number;
  total_pairs?: number;
  teacher_mean?: number;
  llm_mean?: number;
  teacher_std?: number;
  llm_std?: number;
  sanitization_stats?: {
    sanitization_applied: boolean;
    original_rows?: number;
    final_rows?: number;
    removed_rows?: number;
    retention_rate_pct?: number;
  };
}

export interface EvaluationDownloadResponse {
  status: string;
  eval_runid: string;
  download_url: string;
  expires_in: number;
  s3_key: string;
}

export interface DuplicateCheckResponse {
  status: string;
  exists: boolean;
  existing_key?: string;
  existing_parquet_key?: string;
  filename?: string;
  upload_date?: string;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use(async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // API endpoint methods

  /**
   * Get a summary of the data
   * @returns Promise with data summary
   */
  async getDataSummary(): Promise<any> {
    const response: AxiosResponse = await this.client.get('/api/data/summary');
    return response.data;
  }

  /**
   * Get a summary of the rubric
   * @returns Promise with rubric summary
   */
  async getRubricSummary(): Promise<any> {
    const response: AxiosResponse = await this.client.get('/api/rubric/summary');
    return response.data;
  }

  /**
   * Run a complete evaluation including batch scoring and QWK analysis
   * @param params Scoring request parameters (can include dataset_s3_path)
   * @returns Promise with evaluation results
   */
  async runWpEvaluation(params: ScoringRequestParams & { dataset_s3_path?: string } = {}): Promise<ScoringResponse> {
    const response: AxiosResponse<ScoringResponse> = await this.client.post('/api/wpevalrun', params);
    return response.data;
  }

  /**
   * Trigger the scoring process
   * @param params Scoring request parameters
   * @returns Promise with scoring status
   */
  async runScoring(params: ScoringRequestParams = {}): Promise<ScoringResponse> {
    const response: AxiosResponse<ScoringResponse> = await this.client.post('/api/score', params);
    return response.data;
  }

  /**
   * Get scoring results with pagination and filtering
   * @param params Results query parameters
   * @returns Promise with results data
   */
  async getResults(params: ResultsQueryParams = {}): Promise<ResultsResponse> {
    const response: AxiosResponse<ResultsResponse> = await this.client.get('/api/results', {
      params,
    });
    return response.data;
  }

  /**
   * Get statistics about scoring results
   * @returns Promise with stats data
   */
  async getStats(): Promise<StatsResponse> {
    const response: AxiosResponse<StatsResponse> = await this.client.get('/api/stats');
    return response.data;
  }

  /**
   * Get details about a specific response
   * @param requestId The request ID to fetch
   * @returns Promise with response details
   */
  async getResponseDetails(requestId: string): Promise<any> {
    const response: AxiosResponse = await this.client.get(`/api/response/${requestId}`);
    return response.data;
  }

  /**
   * Get a sample of the data
   * @param size Number of samples to fetch
   * @returns Promise with sample data
   */
  async getSampleData(size: number = 5): Promise<SampleResponse> {
    const response: AxiosResponse<SampleResponse> = await this.client.get('/api/sample', {
      params: { size },
    });
    return response.data;
  }

  /**
   * Get a random record for a specific grade
   * @param grade Grade level (defaults to 4)
   * @returns Promise with random record data
   */
  async getRandomRecord(grade: number = 4): Promise<RandomRecord> {
    const response: AxiosResponse<RandomRecord> = await this.client.get('/api/random-record', {
      params: { grade },
    });
    return response.data;
  }

  /**
   * Score a single student response
   * @param data The response data to score
   * @returns Promise with scoring result
   */
  async scoreResponse(data: RandomRecord): Promise<ScoringResult> {
    const response: AxiosResponse<ScoringResult> = await this.client.post('/api/score-response', data);
    return response.data;
  }

  /**
   * Get rubric data for a specific grade
   * @param grade Grade level
   * @returns Promise with rubric data
   */
  public async getRubricData(grade: number): Promise<RubricData> {
    const response: AxiosResponse<{ status: string; rubrics: RubricData['rubrics'] }> = 
      await this.client.get('/api/rubric', {
        params: { grade },
      });
    
    if (response.data.status === 'success') {
      return { rubrics: response.data.rubrics };
    } else {
      throw new Error('Failed to load rubric data');
    }
  }

  /**
   * Get the current model configuration
   * @returns Promise with model configuration
   */
  async getModelConfig(): Promise<ModelConfig> {
    try {
      const response: AxiosResponse<ModelConfig> = await this.client.get('/api/model/config');
      if (response.data && response.data.status === 'success') {
        return response.data;
      } else {
        throw new Error('Invalid response format from model config endpoint');
      }
    } catch (error: any) {
      // Re-throw with more context
      if (error.response) {
        throw new Error(error.response.data?.error || `Server error: ${error.response.status}`);
      } else if (error.request) {
        throw new Error('No response from server. Is the backend running?');
      } else {
        throw error;
      }
    }
  }

  /**
   * Request presigned URL for file upload
   * @param filename Name of the file to upload
   * @param contentType Content type of the file
   * @param fileHash Optional file hash for duplicate detection
   * @returns Promise with presigned URL and upload fields
   */
  async requestUploadUrl(
    filename: string,
    contentType: string = 'text/csv',
    fileHash?: string
  ): Promise<UploadUrlResponse> {
    const response: AxiosResponse<UploadUrlResponse> = await this.client.post(
      '/api/datasets/upload-url',
      { filename, content_type: contentType, file_hash: fileHash }
    );
    return response.data;
  }

  /**
   * Notify backend of completed upload
   * @param s3Key S3 key of the uploaded file
   * @param fileHash File hash
   * @returns Promise with upload completion response
   */
  async notifyUploadComplete(s3Key: string, fileHash: string): Promise<UploadCompleteResponse> {
    const response: AxiosResponse<UploadCompleteResponse> = await this.client.post(
      '/api/datasets/upload-complete',
      { s3_key: s3Key, file_hash: fileHash }
    );
    return response.data;
  }

  /**
   * Check if S3 datasets bucket is configured
   * @returns Promise with configuration status
   */
  async checkDatasetsConfig(): Promise<{ status: string; configured: boolean; message: string; bucket_name?: string }> {
    const response: AxiosResponse<{ status: string; configured: boolean; message: string; bucket_name?: string }> = 
      await this.client.get('/api/datasets/config');
    return response.data;
  }

  /**
   * List available datasets
   * @param type Type of datasets to list ('csv', 'parquet', or 'all')
   * @returns Promise with list of datasets
   */
  async listDatasets(type: 'csv' | 'parquet' | 'all' = 'all'): Promise<DatasetsResponse> {
    const response: AxiosResponse<DatasetsResponse> = await this.client.get('/api/datasets', {
      params: { type },
    });
    return response.data;
  }

  /**
   * Get metadata for a specific dataset
   * @param datasetId S3 key of the dataset
   * @returns Promise with dataset metadata
   */
  async getDatasetMetadata(datasetId: string): Promise<{ status: string; metadata: DatasetMetadata }> {
    const response: AxiosResponse<{ status: string; metadata: DatasetMetadata }> = await this.client.get(
      `/api/datasets/${encodeURIComponent(datasetId)}/metadata`
    );
    return response.data;
  }

  /**
   * Check if a file hash already exists (duplicate detection)
   * @param fileHash SHA256 hash of the file
   * @returns Promise with duplicate check response
   */
  async checkDuplicate(fileHash: string): Promise<DuplicateCheckResponse> {
    const response: AxiosResponse<DuplicateCheckResponse> = await this.client.post(
      '/api/datasets/check-duplicate',
      { file_hash: fileHash }
    );
    return response.data;
  }

  /**
   * Request presigned URLs for multiple file uploads
   * @param files Array of file information with filename, file_type, and optional file_hash
   * @returns Promise with presigned URLs for each file
   */
  async requestMultipleUploadUrls(files: MultiUploadFile[]): Promise<MultiUploadUrlResponse> {
    const response: AxiosResponse<MultiUploadUrlResponse> = await this.client.post(
      '/api/datasets/upload-urls',
      { files }
    );
    return response.data;
  }

  /**
   * Notify backend of completed multi-file upload and trigger join/conversion
   * @param files Array of uploaded file information
   * @param joinConfig Join configuration
   * @param columnMappings Column mappings
   * @param datasetName Optional dataset name
   * @param splitConfig Optional splitting configuration
   * @returns Promise with completion response
   */
  async notifyMultiUploadComplete(
    files: MultiUploadCompleteRequest['files'],
    joinConfig: MultiUploadCompleteRequest['join_config'],
    columnMappings: MultiUploadCompleteRequest['column_mappings'],
    datasetName?: string,
    splitConfig?: MultiUploadCompleteRequest['split_config']
  ): Promise<MultiUploadCompleteResponse> {
    const response: AxiosResponse<MultiUploadCompleteResponse> = await this.client.post(
      '/api/datasets/upload-complete-multi',
      { 
        files, 
        join_config: joinConfig, 
        column_mappings: columnMappings, 
        dataset_name: datasetName,
        split_config: splitConfig
      }
    );
    return response.data;
  }

  /**
   * Get column names from a CSV file in S3
   * @param s3Key S3 key of the CSV file
   * @returns Promise with column names
   */
  async getFileColumns(s3Key: string): Promise<{ status: string; columns: string[]; count: number }> {
    const response: AxiosResponse<{ status: string; columns: string[]; count: number }> = await this.client.get(
      `/api/datasets/${encodeURIComponent(s3Key)}/columns`
    );
    return response.data;
  }

  /**
   * Preview join result before final conversion
   * @param files Array of file information
   * @param joinConfig Join configuration
   * @param columnMappings Column mappings
   * @returns Promise with preview data (sample rows)
   */
  async previewJoin(
    files: MultiUploadCompleteRequest['files'],
    joinConfig: MultiUploadCompleteRequest['join_config'],
    columnMappings: MultiUploadCompleteRequest['column_mappings']
  ): Promise<{ status: string; preview_rows: any[]; row_count: number; columns: string[] }> {
    // For now, this will be a simple endpoint that does a small join
    // In the future, we could optimize this to only process a sample
    const response: AxiosResponse<{ status: string; preview_rows: any[]; row_count: number; columns: string[] }> = 
      await this.client.post(
        '/api/datasets/preview-join',
        { files, join_config: joinConfig, column_mappings: columnMappings, limit: 5 }
      );
    return response.data;
  }

  /**
   * Upload file to S3 using presigned URL
   * @param file File object to upload
   * @param presignedUrl Presigned POST URL
   * @param fields Form fields for presigned POST
   * @param onProgress Optional progress callback
   * @returns Promise that resolves when upload completes
   */
  async uploadFileToS3(
    file: File,
    presignedUrl: string,
    fields: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      
      // Add all fields from presigned POST
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      // Add the file last (must be last field)
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      xhr.open('POST', presignedUrl);
      xhr.send(formData);
    });
  }

  /**
   * Manually trigger CSV to Parquet conversion
   * @param datasetId S3 key of the CSV file
   * @returns Promise with conversion status
   */
  async triggerConversion(datasetId: string): Promise<{ status: string; message: string; parquet_key: string }> {
    const response: AxiosResponse<{ status: string; message: string; parquet_key: string }> = await this.client.post(
      `/api/datasets/${encodeURIComponent(datasetId)}/convert`
    );
    return response.data;
  }

  /**
   * Rename a dataset
   * @param datasetId S3 key of the dataset
   * @param name New name for the dataset
   * @returns Promise with rename response
   */
  async renameDataset(datasetId: string, name: string): Promise<{ status: string; message: string; name: string }> {
    const response: AxiosResponse<{ status: string; message: string; name: string }> = await this.client.post(
      `/api/datasets/${encodeURIComponent(datasetId)}/rename`,
      { name }
    );
    return response.data;
  }

  /**
   * List all evaluation results from S3
   * @returns Promise with list of evaluation results
   */
  async listEvaluationResults(): Promise<EvaluationResultsResponse> {
    const response: AxiosResponse<EvaluationResultsResponse> = await this.client.get(
      '/api/results/list'
    );
    return response.data;
  }

  /**
   * Get detailed metadata for a specific evaluation run
   * @param evalRunId Evaluation run ID
   * @returns Promise with evaluation metadata
   */
  async getEvaluationMetadata(evalRunId: string): Promise<EvaluationMetadataResponse> {
    const response: AxiosResponse<EvaluationMetadataResponse> = await this.client.get(
      `/api/results/${encodeURIComponent(evalRunId)}/metadata`
    );
    return response.data;
  }

  /**
   * Get presigned URL for downloading evaluation result parquet file
   * @param evalRunId Evaluation run ID
   * @param expiresIn Optional expiration time in seconds
   * @returns Promise with download URL
   */
  async downloadEvaluationResult(
    evalRunId: string,
    expiresIn?: number
  ): Promise<EvaluationDownloadResponse> {
    const params = expiresIn ? { expires_in: expiresIn } : {};
    const response: AxiosResponse<EvaluationDownloadResponse> = await this.client.get(
      `/api/results/${encodeURIComponent(evalRunId)}/download`,
      { params }
    );
    return response.data;
  }

  // Job-related API methods

  /**
   * Create a new evaluation job
   * @param params Evaluation parameters
   * @returns Promise with job creation response
   */
  async createEvaluation(params: ScoringRequestParams & { dataset_s3_path?: string }): Promise<CreateEvaluationResponse> {
    const response: AxiosResponse<CreateEvaluationResponse> = await this.client.post('/api/evaluations', params);
    return response.data;
  }

  /**
   * Get detailed status and progress for a specific evaluation job
   * @param jobId Job ID
   * @returns Promise with job details
   */
  async getEvaluation(jobId: string): Promise<EvaluationJob> {
    const response: AxiosResponse<EvaluationJobResponse> = await this.client.get(
      `/api/evaluations/${encodeURIComponent(jobId)}`
    );
    return response.data.job;
  }

  /**
   * List all evaluation jobs with optional filtering and pagination
   * @param status Optional status filter ('pending', 'running', 'completed', 'failed')
   * @param limit Number of jobs per page (max 100)
   * @param offset Offset for pagination
   * @returns Promise with paginated job list
   */
  async listEvaluations(status?: string, limit: number = 50, offset: number = 0): Promise<EvaluationListResponse> {
    const params: any = { limit, offset };
    if (status) {
      params.status = status;
    }
    const response: AxiosResponse<EvaluationListResponse> = await this.client.get(
      '/api/evaluations',
      { params }
    );
    return response.data;
  }

  /**
   * Cancel a running or pending evaluation job
   * @param jobId Job ID to cancel
   * @returns Promise with cancellation response
   */
  async cancelEvaluation(jobId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.delete(
      `/api/evaluations/${encodeURIComponent(jobId)}`
    );
    return response.data;
  }

  // ============================================================================
  // CR (Constructed Response) Evaluation API Methods
  // ============================================================================

  /**
   * Validate CR dataset schema
   * @param s3Key S3 key or path to dataset
   * @returns Promise with validation results
   */
  async validateCRDataset(s3Key: string): Promise<CRValidationResponse> {
    const response: AxiosResponse<CRValidationResponse> = await this.client.post(
      '/api/cr/datasets/validate',
      { s3_key: s3Key }
    );
    return response.data;
  }

  /**
   * Validate CR column mapping and get preview data
   * @param s3Key S3 key to dataset
   * @param columnMapping Mapping of required columns to source columns
   * @returns Promise with validation and preview data
   */
  async validateCRColumnMapping(
    s3Key: string,
    columnMapping: Record<string, string>
  ): Promise<CRMappingValidationResponse> {
    const response: AxiosResponse<CRMappingValidationResponse> = await this.client.post(
      '/api/cr/datasets/validate-mapping',
      { s3_key: s3Key, column_mapping: columnMapping }
    );
    return response.data;
  }

  /**
   * Save CR column mapping and create mapped dataset
   * @param s3Key S3 key to original dataset
   * @param columnMapping Column mapping configuration
   * @returns Promise with mapped dataset S3 key
   */
  async saveCRColumnMapping(
    s3Key: string,
    columnMapping: Record<string, string>
  ): Promise<CRSaveMappingResponse> {
    const response: AxiosResponse<CRSaveMappingResponse> = await this.client.post(
      '/api/cr/datasets/save-mapping',
      { s3_key: s3Key, column_mapping: columnMapping }
    );
    return response.data;
  }

  /**
   * Create a new CR evaluation job
   * @param params CR evaluation parameters
   * @returns Promise with job creation response
   */
  async createCREvaluation(params: CREvaluationParams): Promise<CRJobCreationResponse> {
    const response: AxiosResponse<CRJobCreationResponse> = await this.client.post(
      '/api/cr/evaluations',
      params
    );
    return response.data;
  }

  /**
   * List available CR prompt templates
   * @returns Promise with list of prompt templates
   */
  async listCRPromptTemplates(): Promise<CRPromptTemplatesResponse> {
    const response: AxiosResponse<CRPromptTemplatesResponse> = await this.client.get(
      '/api/cr/prompt-templates'
    );
    return response.data;
  }

  /**
   * Create a new CR prompt template
   * @param params Template name, description, and content
   * @returns Promise with creation response
   */
  async createCRPromptTemplate(params: CRCreateTemplateParams): Promise<CRCreateTemplateResponse> {
    const response: AxiosResponse<CRCreateTemplateResponse> = await this.client.post(
      '/api/cr/prompt-templates',
      params
    );
    return response.data;
  }

  /**
   * List available CR models
   * @returns Promise with list of models
   */
  async listCRModels(): Promise<CRModelsResponse> {
    const response: AxiosResponse<CRModelsResponse> = await this.client.get(
      '/api/cr/models'
    );
    return response.data;
  }

  /**
   * List available Bedrock models with thinking-capability metadata
   * @returns Promise with list of models
   */
  async listBedrockModels(): Promise<BedrockModelsResponse> {
    const response: AxiosResponse<BedrockModelsResponse> = await this.client.get(
      '/api/bedrock/models'
    );
    return response.data;
  }

  /**
   * Alias for requestUploadUrl - used by dataset upload components
   * @param filename Name of the file to upload
   * @param contentType Content type of the file
   * @param fileHash Optional file hash for duplicate detection
   * @returns Promise with presigned URL and upload fields
   */
  async getDatasetUploadUrl(
    filename: string,
    contentType: string = 'text/csv',
    fileHash?: string
  ): Promise<UploadUrlResponse> {
    return this.requestUploadUrl(filename, contentType, fileHash);
  }

  /**
   * Alias for notifyUploadComplete - used by dataset upload components
   * @param s3Key S3 key of the uploaded file
   * @param filename Original filename
   * @param fileHash File hash
   * @returns Promise with upload completion response
   */
  async notifyDatasetUploadComplete(
    s3Key: string,
    filename: string,
    fileHash: string
  ): Promise<UploadCompleteResponse> {
    return this.notifyUploadComplete(s3Key, fileHash);
  }

  // ============================================================================
  // Prompt Management API Methods
  // ============================================================================

  /**
   * List all prompt templates
   * @param templateType Optional filter by template type ('WP', 'CR', 'SYSTEM')
   * @param status Optional filter by status ('active', 'archived')
   * @returns Promise with list of templates
   */
  async listPromptTemplates(templateType?: string, status?: string): Promise<{ status: string; templates: any[] }> {
    const params: any = {};
    if (templateType) params.template_type = templateType;
    if (status) params.status = status;
    const response: AxiosResponse<{ status: string; templates: any[] }> = await this.client.get(
      '/api/admin/prompts/templates',
      { params }
    );
    return response.data;
  }

  /**
   * Get a single prompt template by ID
   * @param templateId Template ID
   * @returns Promise with template details
   */
  async getPromptTemplate(templateId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.client.get(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}`
    );
    return response.data;
  }

  /**
   * Create a new prompt template
   * @param data Template data (template_name, template_type, content, description)
   * @returns Promise with creation response
   */
  async createPromptTemplate(data: {
    template_name: string;
    template_type: string;
    content: string;
    description?: string;
    model_id?: string | null;
    max_tokens?: number | null;
    temperature?: number | null;
    thinking_enabled?: boolean | null;
    thinking_budget?: number | null;
  }): Promise<{ status: string; template: any; message: string }> {
    const response: AxiosResponse<{ status: string; template: any; message: string }> = await this.client.post(
      '/api/admin/prompts/templates',
      data
    );
    return response.data;
  }

  /**
   * Update an existing prompt template
   * @param templateId Template ID
   * @param data Update data (content, description)
   * @returns Promise with update response
   */
  async updatePromptTemplate(
    templateId: string,
    data: {
      content?: string;
      description?: string;
      model_id?: string | null;
      max_tokens?: number | null;
      temperature?: number | null;
      thinking_enabled?: boolean | null;
      thinking_budget?: number | null;
    }
  ): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.put(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}`,
      data
    );
    return response.data;
  }

  /**
   * Archive a prompt template
   * @param templateId Template ID
   * @returns Promise with archive response
   */
  async archivePromptTemplate(templateId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.delete(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}`
    );
    return response.data;
  }

  /**
   * Set a template as the default for its type
   * @param templateId Template ID
   * @param templateType Template type
   * @returns Promise with response
   */
  async setDefaultTemplate(templateId: string, templateType: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.post(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}/set-default`,
      { template_type: templateType }
    );
    return response.data;
  }

  /**
   * List all attribute mappings
   * @param templateType Optional filter by template type
   * @param status Optional filter by status
   * @returns Promise with list of mappings
   */
  async listAttributeMappings(templateType?: string, status?: string): Promise<{ status: string; mappings: any[] }> {
    const params: any = {};
    if (templateType) params.template_type = templateType;
    if (status) params.status = status;
    const response: AxiosResponse<{ status: string; mappings: any[] }> = await this.client.get(
      '/api/admin/prompts/mappings',
      { params }
    );
    return response.data;
  }

  /**
   * Create a new attribute mapping
   * @param data Mapping data (attribute_name, attribute_value, template_type, template_id, priority)
   * @returns Promise with creation response
   */
  async createAttributeMapping(data: {
    attribute_name: string;
    attribute_value: string;
    template_type: string;
    template_id: string;
    priority?: number;
  }): Promise<{ status: string; mapping: any; message: string }> {
    const response: AxiosResponse<{ status: string; mapping: any; message: string }> = await this.client.post(
      '/api/admin/prompts/mappings',
      data
    );
    return response.data;
  }

  /**
   * Update mapping priority
   * @param mappingId Mapping ID
   * @param priority New priority value
   * @returns Promise with update response
   */
  async updateMappingPriority(mappingId: string, priority: number): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.put(
      `/api/admin/prompts/mappings/${encodeURIComponent(mappingId)}/priority`,
      { priority }
    );
    return response.data;
  }

  /**
   * Delete an attribute mapping
   * @param mappingId Mapping ID
   * @returns Promise with deletion response
   */
  async deleteAttributeMapping(mappingId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.delete(
      `/api/admin/prompts/mappings/${encodeURIComponent(mappingId)}`
    );
    return response.data;
  }

  /**
   * Get template usage information (which mappings use this template)
   * @param templateId Template ID
   * @returns Promise with usage information
   */
  async getTemplateUsage(templateId: string): Promise<{ status: string; mappings: any[] }> {
    const response: AxiosResponse<{ status: string; mappings: any[] }> = await this.client.get(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}/usage`
    );
    return response.data;
  }

  /**
   * Get available variables for a template type
   * @param templateType Template type ('WP', 'CR', 'SYSTEM')
   * @returns Promise with list of available variables
   */
  async getAvailableVariables(templateType: string): Promise<{ status: string; variables: string[] }> {
    const response: AxiosResponse<{ status: string; variables: string[] }> = await this.client.get(
      `/api/admin/prompts/variables/${encodeURIComponent(templateType)}`
    );
    return response.data;
  }

  /**
   * List version history for a template, newest first
   * @param templateId Template ID
   * @returns Promise with list of version snapshots
   */
  async listTemplateVersions(templateId: string): Promise<{ status: string; versions: any[] }> {
    const response: AxiosResponse<{ status: string; versions: any[] }> = await this.client.get(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}/versions`
    );
    return response.data;
  }

  /**
   * Revert a template's content to a prior version (creates a new version, nothing is deleted)
   * @param templateId Template ID
   * @param versionNumber Version number to restore content from
   * @param updatedBy Optional admin identifier
   * @returns Promise with the reverted template
   */
  async revertTemplateVersion(
    templateId: string,
    versionNumber: number,
    updatedBy?: string
  ): Promise<{ status: string; message: string; template: any }> {
    const response: AxiosResponse<{ status: string; message: string; template: any }> = await this.client.post(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}/revert/${versionNumber}`,
      updatedBy ? { updated_by: updatedBy } : {}
    );
    return response.data;
  }

  /**
   * Capture the template's current attribute mappings onto its current version's history row.
   * Call right after a save action finishes creating/deleting mappings, so revert can restore them later.
   * @param templateId Template ID
   * @returns Promise with response
   */
  async snapshotTemplateMappings(templateId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.post(
      `/api/admin/prompts/templates/${encodeURIComponent(templateId)}/snapshot-mappings`
    );
    return response.data;
  }

  /**
   * List all variables (global registry)
   * @returns Promise resolving to variables list
   */
  async listVariables(): Promise<{ status: string; variables: any[] }> {
    const response: AxiosResponse<{ status: string; variables: any[] }> = await this.client.get(
      '/api/admin/variables'
    );
    return response.data;
  }

  /**
   * Create a new custom variable (available to all templates)
   * @param data Variable data
   * @returns Promise resolving to created variable info
   */
  async createVariable(data: {
    variable_name: string;
    description: string;
    default_value?: string;
  }): Promise<{ status: string; variable_id: string; message: string }> {
    const response: AxiosResponse<{ status: string; variable_id: string; message: string }> = await this.client.post(
      '/api/admin/variables',
      data
    );
    return response.data;
  }

  /**
   * Update an existing custom variable
   * @param variableId ID of variable to update
   * @param data Updated variable data
   * @returns Promise resolving to success message
   */
  async updateVariable(
    variableId: string,
    data: {
      variable_name?: string;
      description?: string;
      default_value?: string;
    }
  ): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.put(
      `/api/admin/variables/${encodeURIComponent(variableId)}`,
      data
    );
    return response.data;
  }

  /**
   * Delete a custom variable
   * @param variableId ID of variable to delete
   * @returns Promise resolving to success message
   */
  async deleteVariable(variableId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> = await this.client.delete(
      `/api/admin/variables/${encodeURIComponent(variableId)}`
    );
    return response.data;
  }

  // ── Product Management (SCINFRA-1088) ──────────────────────────────────

  /** List all products (metadata only — never the secret). */
  async listProducts(): Promise<{ status: string; products: Product[] }> {
    const response: AxiosResponse<{ status: string; products: Product[] }> =
      await this.client.get('/api/admin/products');
    return response.data;
  }

  /**
   * Create a product and generate its JWT shared secret.
   * The returned `secret` is shown ONCE and cannot be retrieved later.
   */
  async createProduct(params: {
    product_id: string;
    name: string;
    default_callback_url?: string;
  }): Promise<ProductSecretResponse> {
    const response: AxiosResponse<ProductSecretResponse> = await this.client.post(
      '/api/admin/products',
      params
    );
    return response.data;
  }

  /** Update product metadata (name, default callback URL, active flag). */
  async updateProduct(
    productId: string,
    params: { name?: string; default_callback_url?: string; active?: boolean }
  ): Promise<{ status: string; product: Product }> {
    const response: AxiosResponse<{ status: string; product: Product }> =
      await this.client.put(`/api/admin/products/${encodeURIComponent(productId)}`, params);
    return response.data;
  }

  /** Delete a product and its shared secret. */
  async deleteProduct(productId: string): Promise<{ status: string; message: string }> {
    const response: AxiosResponse<{ status: string; message: string }> =
      await this.client.delete(`/api/admin/products/${encodeURIComponent(productId)}`);
    return response.data;
  }

  /** Generate a new shared secret for a product (shown once). */
  async rotateProductSecret(productId: string): Promise<ProductSecretResponse> {
    const response: AxiosResponse<ProductSecretResponse> = await this.client.post(
      `/api/admin/products/${encodeURIComponent(productId)}/rotate-secret`
    );
    return response.data;
  }

  /**
   * Generic error handler for API requests
   * @param error Error object
   * @returns Rejected promise with formatted error
   */
  handleError(error: any): Promise<never> {
    // Log the error for debugging
    console.error('API error:', error);

    // Format error message
    let errorMessage = 'An unexpected error occurred';
    if (error.response) {
      // Server responded with an error status
      const serverError = error.response.data.error || error.response.statusText;
      errorMessage = `Server error: ${serverError} (${error.response.status})`;
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      // Something else happened while setting up the request
      errorMessage = error.message || errorMessage;
    }

    return Promise.reject(new Error(errorMessage));
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

// Export individual methods for easier imports (bound to the instance)
export const listPromptTemplates = apiService.listPromptTemplates.bind(apiService);
export const getPromptTemplate = apiService.getPromptTemplate.bind(apiService);
export const createPromptTemplate = apiService.createPromptTemplate.bind(apiService);
export const updatePromptTemplate = apiService.updatePromptTemplate.bind(apiService);
export const archivePromptTemplate = apiService.archivePromptTemplate.bind(apiService);
export const setDefaultTemplate = apiService.setDefaultTemplate.bind(apiService);
export const listAttributeMappings = apiService.listAttributeMappings.bind(apiService);
export const createAttributeMapping = apiService.createAttributeMapping.bind(apiService);
export const updateMappingPriority = apiService.updateMappingPriority.bind(apiService);
export const deleteAttributeMapping = apiService.deleteAttributeMapping.bind(apiService);
export const getTemplateUsage = apiService.getTemplateUsage.bind(apiService);
export const getAvailableVariables = apiService.getAvailableVariables.bind(apiService);
export const listTemplateVersions = apiService.listTemplateVersions.bind(apiService);
export const revertTemplateVersion = apiService.revertTemplateVersion.bind(apiService);
export const snapshotTemplateMappings = apiService.snapshotTemplateMappings.bind(apiService);
export const listBedrockModels = apiService.listBedrockModels.bind(apiService);
export const listVariables = apiService.listVariables.bind(apiService);
export const createVariable = apiService.createVariable.bind(apiService);
export const updateVariable = apiService.updateVariable.bind(apiService);
export const deleteVariable = apiService.deleteVariable.bind(apiService);
export const listProducts = apiService.listProducts.bind(apiService);
export const createProduct = apiService.createProduct.bind(apiService);
export const updateProduct = apiService.updateProduct.bind(apiService);
export const deleteProduct = apiService.deleteProduct.bind(apiService);
export const rotateProductSecret = apiService.rotateProductSecret.bind(apiService); 